// src/components/resources/tools/generators/EmailThread/GmailPreview.tsx

'use client'

import { useRef } from 'react'
import {
  Archive, MoreVertical, Star, ChevronLeft, RefreshCw, Search, Menu, Settings, HelpCircle, Inbox, Send, File, Trash2, Clock, Mail, Edit3, Paperclip, Image as ImageIcon, Smile, Link2
} from 'lucide-react'
import { clsx } from 'clsx'
import NextImage from 'next/image'
import { GmailMessageRow } from './GmailMessageRow'
import type { EmailConfigChange, EmailThreadConfig } from './types'

/**
 * Gmail-style thread preview component.
 */
export function GmailPreview({
  config,
  onConfigChange,
  onToggleMessageStar,
}: {
  config: EmailThreadConfig
  onConfigChange: EmailConfigChange
  onToggleMessageStar: (messageId: string) => void
}): JSX.Element {
  const threadRef = useRef<HTMLDivElement>(null)

  const handleToggleThreadStar = (): void => {
    onConfigChange(prev => ({ starred: !prev.starred }))
  }

  const handleToggleArchived = (): void => {
    onConfigChange(prev => ({ archived: !prev.archived }))
  }

  return (
    <div
      ref={threadRef}
      id="email-thread-preview"
      className="flex h-full w-full flex-col bg-white dark:bg-slate-900"
      style={{ borderRadius: 18, boxShadow: '0 2px 16px 0 rgba(0,0,0,0.06)' }}
    >
      {/* Top Header */}
      <header className="flex flex-wrap items-center gap-2 sm:gap-4 border-b border-gray-200 dark:border-gray-700 px-2 sm:px-4 py-2 w-full">
        {/* Hamburger */}
        <button
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0"
          onClick={() => onConfigChange(prev => ({ sidebarCollapsed: !prev.sidebarCollapsed }))}
        >
          <Menu className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
        {/* Gmail Logo */}
        <NextImage
          src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23EA4335'%3E%3Cpath d='M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z'/%3E%3C/svg%3E"
          alt="Gmail logo"
          width={40}
          height={40}
          className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0"
          priority
        />
        {/* Search bar */}
        <div className="flex flex-1 min-w-0 items-center gap-2 rounded-lg bg-gray-100 dark:bg-gray-800 px-2 sm:px-4 py-2 mt-2 sm:mt-0">
          <Search className="h-5 w-5 text-gray-600 dark:text-gray-400 flex-shrink-0" />
          <input
            type="text"
            placeholder="Search mail"
            className="flex-1 min-w-0 bg-white dark:bg-gray-900 text-xs outline-none text-gray-900 dark:text-gray-100 placeholder:text-gray-500 dark:placeholder:text-gray-500"
            aria-label="Search mail"
          />
        </div>
        {/* Help icon (hidden on mobile) */}
        <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0 hidden sm:inline-flex" aria-label="Help">
          <HelpCircle className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
        {/* Settings icon */}
        <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0" aria-label="Settings">
          <Settings className="h-5 w-5 text-gray-600 dark:text-gray-400" />
        </button>
        {/* User avatar */}
        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-700 text-sm font-medium border border-gray-300 flex-shrink-0" aria-label="User avatar">
          M
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* Left Sidebar */}
        {!config.sidebarCollapsed && (
          <aside className="hidden sm:block w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 p-2">
            <button className="mb-4 flex items-center gap-3 rounded-2xl bg-blue-50 dark:bg-blue-900/30 px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-200 shadow hover:shadow-md">
              <Edit3 className="h-5 w-5" />
              Compose
            </button>

            <nav className="space-y-1" aria-label="Sidebar folders">
              <button className="flex w-full items-center gap-3 rounded-r-full bg-red-50 dark:bg-red-900/30 px-4 py-2 text-sm font-medium text-gray-900 dark:text-gray-100">
                <Inbox className="h-5 w-5" />
                <span>Inbox</span>
                <span className="ml-auto text-gray-900 dark:text-gray-100">3</span>
              </button>
              <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-r-full">
                <Star className="h-5 w-5" />
                <span>Starred</span>
              </button>
              <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-r-full">
                <Clock className="h-5 w-5" />
                <span>Snoozed</span>
              </button>
              <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-r-full">
                <Send className="h-5 w-5" />
                <span>Sent</span>
              </button>
              <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-r-full">
                <File className="h-5 w-5" />
                <span>Drafts</span>
              </button>
              <button className="flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-r-full">
                <Trash2 className="h-5 w-5" />
                <span>Trash</span>
              </button>
            </nav>
          </aside>
        )}

        {/* Main Content */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Conversation Header */}
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700 px-2 sm:px-4 py-2 sm:py-3 min-w-0">
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
              <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full" aria-label="Back">
                <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <button
                className={clsx(
                  'p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full',
                  config.archived ? 'bg-gray-100 dark:bg-gray-800' : ''
                )}
                onClick={handleToggleArchived}
                aria-label={config.archived ? 'Unarchive thread' : 'Archive thread'}
              >
                <Archive className={clsx(
                  'h-5 w-5',
                  config.archived ? 'text-green-600 dark:text-green-400' : 'text-gray-600 dark:text-gray-400'
                )} />
              </button>
              <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full" aria-label="Delete">
                <Trash2 className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full" aria-label="Mark as read">
                <Mail className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full" aria-label="Snooze">
                <Clock className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
              <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full" aria-label="More actions">
                <MoreVertical className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0 min-w-0">
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 truncate max-w-[80px] sm:max-w-none">{`1-${config.messages.length} of ${config.messages.length}`}</span>
              {/* Only show refresh button on desktop */}
              <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full flex-shrink-0 hidden sm:inline-flex" aria-label="Refresh">
                <RefreshCw className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>

          {/* Thread Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6" id="email-thread-inner-preview">
            <div className="flex items-start justify-between mb-6">
              <h2 className="text-2xl text-gray-900 dark:text-gray-100">{config.subject}</h2>
              <button
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                onClick={handleToggleThreadStar}
                aria-label="Star thread"
              >
                <Star className={clsx(
                  'h-5 w-5',
                  config.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 dark:text-gray-500'
                )} />
              </button>
            </div>

            <div className="space-y-4">
              {config.messages.map((message, idx) => (
                <GmailMessageRow
                  key={message.id}
                  message={message}
                  isLast={idx === config.messages.length - 1}
                  showAvatars={config.showAvatars !== false}
                  highlightReply={Boolean(config.highlightReply)}
                  onToggleStar={onToggleMessageStar}
                />
              ))}
            </div>

            {/* Reply Box */}
            <div className="mt-8 border border-gray-300 dark:border-gray-700 rounded-lg shadow-sm">
              <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
                <button className="text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded" aria-label="Reply">Reply</button>
                <button className="text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded" aria-label="Reply all">Reply all</button>
                <button className="text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 px-2 py-1 rounded" aria-label="Forward">Forward</button>
              </div>
              <textarea
                className="w-full px-4 py-3 text-sm outline-none resize-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                rows={6}
                placeholder="Reply..."
                aria-label="Reply message"
              />
              <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 px-4 py-2">
                <div className="flex items-center gap-1">
                  <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Attach file">
                    <Paperclip className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Insert link">
                    <Link2 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Insert emoji">
                    <Smile className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded" aria-label="Insert image">
                    <ImageIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>
                <button className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-6 py-2 rounded" aria-label="Send reply">
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
